// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

// material ui
import { Box, IconButton, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar.tsx';

// feature-utils
import formatDate from '@/features/alarm-hub/utils/formatDate';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';

// relative
import AlarmStateSelect from './AlarmStateSelect.tsx';
import { SeverityBars } from './SeverityBars';

import { DateTime } from 'luxon';

// Map translation keys to API state values
const stateTranslationMap = [
  { key: 'state.unresolved', value: 'Unresolved' },
  { key: 'state.dismissed', value: 'Reject' },
  { key: 'state.resolved', value: 'Solved' },
  { key: 'state.reset', value: 'Reset' },
  { key: 'state.auto_resolved', value: 'AutoResolved' },
];

const AlarmDataGrid = ({ alarms }) => {
  const { t } = useTranslation('alarms');
  const localeText = useDatagridLocaleText();

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `Alarm-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
          rowGroupingModel: [],
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleRowGroupingModelChange = (newModel) => {
    updateChosenPreset({ rowGroupingModel: newModel });
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];

  // Automatically hide columns that are being grouped
  const effectiveVisibilityModel = React.useMemo(() => {
    const groupedColumns = currentPreset.rowGroupingModel || [];
    const visibilityModel = { ...currentPreset.visibilityList };

    // Hide columns that are being grouped
    groupedColumns.forEach((field) => {
      visibilityModel[field] = false;
    });

    return visibilityModel;
  }, [currentPreset.visibilityList, currentPreset.rowGroupingModel]);

  const columns = [
    {
      field: 'title',
      headerName: t('columns.title'),
      flex: 1,
      valueGetter: (_, row) => row?.title ?? row?.Title ?? null,
      renderCell: (params) => {
        const isGroupingRow = params.rowNode?.type === 'group';
        if (isGroupingRow) return null;

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            {/* {alarmTypeIconMap[params.row.alarmType ?? params.row.AlarmType]} */}
            <Typography
              variant="body2"
              noWrap
              sx={{ ml: 1, fontWeight: 'inherit' }} // ⬅️ inherit from the row
            >
              {params.value || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'severity',
      headerName: 'Severity',
      width: 220,
      valueGetter: (_, row) => row?.severity ?? row?.Severity ?? null,
      renderCell: (params) => {
        const isGroupingRow = params.rowNode?.type === 'group';
        if (isGroupingRow) return null;

        const severity = params.value;
        const tooltipText = severity
          ? t(`severity.${String(severity).toLowerCase()}`)
          : '-';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <SeverityBars severity={severity} label={tooltipText} showLabel />
          </Box>
        );
      },
    },
    {
      field: 'created',
      headerName: t('columns.created_at'),
      type: 'dateTime',
      flex: 1,
      valueGetter: (value, row) => {
        const rawValue = value ?? row?.Created;
        if (!rawValue) return null;
        const createdDateTime = DateTime.fromISO(rawValue, { zone: 'utc' });
        return createdDateTime.isValid ? createdDateTime.toJSDate() : null;
      },
      renderCell: (params) => {
        const isGroupingRow = params.rowNode?.type === 'group';
        if (isGroupingRow) return null;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" noWrap>
              {formatDate(params.row?.created ?? params.row?.Created) || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'associations',
      headerName: 'Scope',
      flex: 2,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row?.associations ?? row?.Associations ?? null,
      renderCell: (params) => {
        const isGroupingRow = params.rowNode?.type === 'group';
        if (isGroupingRow) return null;

        if (!params.value || typeof params.value !== 'object') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" noWrap>
                -
              </Typography>
            </Box>
          );
        }

        // Extract all names (or IDs as fallback) from all arrays in the object
        const allNames = Object.values(params.value)
          .flat()
          .map((item) => item?.name || item?.id)
          .filter((name) => name != null)
          .join(', ');

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" noWrap title={allNames || '-'}>
              {allNames || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'state',
      valueGetter: (_, row) => row?.state ?? row?.State ?? null,
      headerName: t('columns.state'),
      width: 170,
      type: 'string',
      groupable: true,
      filterable: false,
      getApplyQuickFilterFn: (value) => {
        if (!value) return null;

        const searchNormalized = value.toLowerCase().trim();

        // Find matching state by checking translated values
        const matchedState = stateTranslationMap.find((mapping) => {
          const translatedValue = t(mapping.key).toLowerCase();
          const englishValue = mapping.value.toLowerCase();

          // Check for exact match or starts with (more literal matching)
          return (
            translatedValue === searchNormalized ||
            translatedValue.startsWith(searchNormalized) ||
            englishValue === searchNormalized ||
            englishValue.startsWith(searchNormalized)
          );
        });

        return (cellValue) => {
          if (!cellValue || typeof cellValue !== 'string') return false;

          // If we found a translation match, check exact state value
          if (matchedState) {
            return cellValue === matchedState.value;
          }

          // Otherwise, do a literal start-of-string match on the cell value
          return cellValue.toLowerCase().startsWith(searchNormalized);
        };
      },
      renderCell: (params) => {
        // Check if this is a grouping row
        const isGroupingRow = params.rowNode?.type === 'group';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <AlarmStateSelect params={params} disabled={isGroupingRow} />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      groupable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isGroupingRow = params.rowNode?.type === 'group';
        if (isGroupingRow) return null;
        return (
          <IconButton
            component={Link}
            to={`${params.row.alarmId ?? params.row.AlarmId}`}
            size="small"
            sx={{
              color: 'action.active',
              '&:hover': {
                color: 'action.active',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <SearchRoundedIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <DataGridPremium
      rows={alarms || []}
      getRowId={(row) => row.alarmId ?? row.AlarmId}
      columns={columns}
      pageSize={10}
      pagination
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 50 } },
      }}
      pageSizeOptions={[50, 100]}
      rowsPerPageOptions={[10, 25, 50]}
      columnVisibilityModel={effectiveVisibilityModel}
      onColumnVisibilityModelChange={handleVisibilityChange}
      onDensityChange={(density) => handleDensityChange(density)}
      density={currentPreset.density}
      sortModel={currentPreset.sortModel}
      onSortModelChange={handleSortModelChange}
      rowGroupingModel={currentPreset.rowGroupingModel || []}
      onRowGroupingModelChange={handleRowGroupingModelChange}
      groupingColDef={{
        hideDescendantCount: false,
      }}
      disableRowSelectionOnClick
      localeText={localeText}
      showToolbar
      slots={{
        toolbar: DataGridToolbar,
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'linear-progress',
        },
        toolbar: {
          showExport: false,
          showColumns: false,
          showFilters: false,
          showDivider: false,
        },
      }}
    />
  );
};

export default AlarmDataGrid;
